/**
 * OperationsCenter — Phase 8e
 *
 * Calm, additive customer-facing surface that composes Phase 8a/8b/8c
 * backend projections into a single section mounted at the bottom of
 * EventDetail.jsx. NOT a replacement for the existing Event Command
 * Center — adds beneath it for users who want a single calm summary.
 *
 * Gating:
 *   * Frontend feature flag `REACT_APP_ENABLE_EVENT_OPERATIONS_CENTER`
 *     must be explicitly set to "true". Default OFF.
 *   * Backend route `GET /api/events/{eventId}/operations` is itself
 *     gated by `ENABLE_EVENT_OPERATIONS_CENTER`. When the backend flag
 *     is off, the route returns 404 — we render nothing.
 *   * Failure modes (404 / 403 / network) → silent hide. The section
 *     never displays an error to the customer.
 *
 * Design principles (per Phase 8 ADR + Phase 8e directive):
 *   • Calm, not busy. No red-dot urgency. No PM-style clutter.
 *   • Mobile-first. Stacks vertically; sections compact on small screens.
 *   • Show only what matters now. Each section gracefully hides when empty.
 *   • Customer-safe fields only — no admin audit language, no rejected
 *     bridge rows, no raw IDs, no worker_attribution, no scores.
 */

import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Sparkles } from 'lucide-react';

import * as featureFlag from './featureFlag';
import EventOverviewSection from './sections/EventOverviewSection';
import NextStepSection from './sections/NextStepSection';
import ServiceProgressSection from './sections/ServiceProgressSection';
import BriefRailSection from './sections/BriefRailSection';
import BridgeTimelineSection from './sections/BridgeTimelineSection';
import PaymentSummarySection from './sections/PaymentSummarySection';
import MilestoneSummarySection from './sections/MilestoneSummarySection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Re-export for callers that want to short-circuit before mounting.
export { isOpsCenterEnabled } from './featureFlag';

export default function OperationsCenter({ eventId, refreshSignal }) {
  // Looked up via the imported namespace so tests can spy on the
  // accessor without struggling against CRA's env-var inlining.
  const enabled = featureFlag.isOpsCenterEnabled();
  const [projection, setProjection] = useState(null);
  const [loading, setLoading] = useState(true);
  // We deliberately *suppress* errors from the customer surface — any
  // failure (404 from flag-off backend, 403, network) results in the
  // section silently rendering nothing. The existing EventDetail
  // experience is untouched in that case.
  const [available, setAvailable] = useState(true);

  const fetchProjection = useCallback(async () => {
    if (!enabled || !eventId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/events/${eventId}/operations`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setProjection(res.data || null);
      setAvailable(true);
    } catch (_err) {
      // Flag off (404) or unauthorized (403) or transient network — hide
      // silently. The customer never sees an error from this section.
      setProjection(null);
      setAvailable(false);
    } finally {
      setLoading(false);
    }
  }, [enabled, eventId]);

  useEffect(() => {
    fetchProjection();
  }, [fetchProjection, refreshSignal]);

  // Flag off OR backend unavailable OR projection missing → render nothing.
  // Existing EventDetail experience is unchanged.
  if (!enabled) return null;
  if (loading && !projection) {
    return (
      <div
        className="rounded-2xl border border-gray-100/80 bg-white p-5"
        data-testid="operations-center-loading"
      >
        <div className="h-4 w-32 bg-gray-100 rounded animate-pulse" />
        <div className="mt-3 space-y-2">
          <div className="h-3 w-full bg-gray-50 rounded animate-pulse" />
          <div className="h-3 w-4/5 bg-gray-50 rounded animate-pulse" />
        </div>
      </div>
    );
  }
  if (!available || !projection) return null;

  return (
    <section
      className="rounded-2xl border border-gray-100/80 bg-white overflow-hidden"
      data-testid="operations-center"
      aria-label="Event Operations Center"
    >
      <header
        className="flex items-center gap-2.5 px-5 pt-5 pb-3"
        data-testid="operations-center-header"
      >
        <Sparkles className="w-4 h-4 text-violet-500" aria-hidden />
        <h2 className="text-sm font-semibold text-gray-900 tracking-tight">
          Your event at a glance
        </h2>
      </header>

      <div className="px-5 pb-5 space-y-5">
        <NextStepSection projection={projection} eventId={eventId} />
        <EventOverviewSection event={projection.event} />
        <ServiceProgressSection items={projection.service_progress} />
        <BriefRailSection rail={projection.brief_rail} eventId={eventId} />
        <BridgeTimelineSection timeline={projection.bridge_timeline} />
        <PaymentSummarySection payments={projection.payments} />
        <MilestoneSummarySection milestones={projection.milestones} />
      </div>
    </section>
  );
}
